import { refreshDocument } from 'client/graphQueries/invoices'
import { ActionCreator } from 'typescript-fsa'

import { getDocument, getDocumentHTML } from '../api/document'
import * as api from '../api/invoice'
import { notifier } from '../components/common/Notifier'
import { Analytics, checkPlacement } from '../Global/Analytics'
import {
  AnalyticsObject,
  AnalyticsType,
  Category,
  Events,
  Placement,
  Result,
  trackingFields,
} from '../Global/AnalyticsEvents'
import { CustomerInterface } from '../Interfaces/CustomerInterface'
import { DocumentTypes, DraftInterface } from '../Interfaces/Draft/DraftInterface'
import { InvoiceInterface } from '../Interfaces/Invoice/InvoiceInterface'
import { downloadDocumentPDF, downloadXRechnung } from '../Other/functions/documentFunctions'
import { handleFieldsBeforeSave } from '../Other/functions/modelFunctions'
import { Immutable } from '../Other/Immutable'
import { InvoiceActions } from './actionTypes'
import { getInvoiceNumberSequences } from './documentNumber'
import {
  changeLines,
  getCountry,
  loadVitalsForEditor,
  navigateAfterSending,
  navigateToEditor,
  sendEInvoice,
  sendPaperInvoice,
  setTypeForInvoiceDraft,
  updateCustomerNumberFromSettings,
  updateDraftCompanyFromCompany,
  updateDraftCompanyFromSettings,
  updateDraftCustomer,
  updateDraftCustomerFromCustomers,
  updateDraftFieldsFromSettings,
  updateDraftForCreditNote,
  updateDraftStylesFromSettings,
  updateInvoiceDraftAction,
  updateInvoiceNumber,
  updatePaymentReferenceFromSettings,
} from './draft'
import { getNavigationToList } from './email'
import { actionCreator, actionCreatorAsync, AsyncAction } from './index'
import { loadingEditorVitals, navigateAddModal, navigateRemoveModal, setNavigation } from './navigation'
import { hideGeneratingDocumentSpinner, showGeneratingDocumentSpinner } from './ongoing'
import { deleteRecurringProfileSettings } from './recurringProfile'

export function deleteInvoice(id: number, documentType: DocumentTypes) {
  return async () => {
    await api.deleteInvoice(id, documentType)
  }
}

export function getInvoice(id: number): AsyncAction<InvoiceInterface> {
  return actionCreatorAsync<InvoiceInterface>(InvoiceActions.GET_INVOICE, async () => api.getInvoice(id))
}

export function saveInvoice(invoice: DraftInterface) {
  return actionCreatorAsync<DraftInterface>(InvoiceActions.SAVE_INVOICE, async () => api.saveInvoice(invoice))
}

export const updateInvoice = (draft: DraftInterface) => {
  const { invoiceId, document } = draft
  Analytics.trackEvent(Events.editDocument, {
    type: AnalyticsType.action,
    category: Category.document,
    result: Result.success,
    placement: checkPlacement(window.location.pathname),
    documentType: AnalyticsObject.invoice,
    documentId: invoiceId,
  })
  return async (dispatch, getState) => {
    const state = getState()
    draft.id = invoiceId
    draft = handleInvoiceBeforeSave(draft, state.config, getCountry(state), state.settings)
    await dispatch(saveInvoice(draft))
    await dispatch(actionCreatorAsync(InvoiceActions.SET_INVOICE, async () => getDocument(document, invoiceId)))
  }
}

export const updateInvoiceAndClose = (invoice: DraftInterface) => {
  return async (dispatch, getState) => {
    dispatch(showGeneratingDocumentSpinner())
    await dispatch(updateInvoice(invoice))
    dispatch(hideGeneratingDocumentSpinner())
  }
}

export const updateInvoiceAndDownloadPdf = (invoice: DraftInterface) => {
  return async (dispatch, getState) => {
    const getOrderData = (order) => {
      return order
        ? {
            projectName: order?.name,
            orderType: order?.orderType,
            orderItemsLength: order?.items?.length,
            scheduleId: order?.scheduleId,
          }
        : null
    }
    const orderData = getOrderData(invoice.order)
    const payload = {
      type: AnalyticsType?.action,
      category: Category?.document,
      result: Result?.success,
      placement: Placement?.preview,
      documentType: AnalyticsObject?.invoice,
      deliveryType: trackingFields?.deliveryType.pdf,
      documentID: invoice?.invoiceId,
      onlinePayments: null,
      orderData,
    }
    dispatch(showGeneratingDocumentSpinner())
    await dispatch(updateInvoice(invoice))
    dispatch(hideGeneratingDocumentSpinner())
    downloadDocumentPDF(getState()?.invoice)
    dispatch(setNavigation(getNavigationToList(invoice?.document, invoice?.id, getState())))
    Analytics.trackEvent(Events?.sendDocument, payload)
  }
}

export const tryUpdateInvoiceAndDownloadXRechnung = (
  documentOrId: DraftInterface | number,
  documentType: DocumentTypes = DocumentTypes.INVOICE,
) => {
  return async (dispatch, getState) => {
    let invoice: DraftInterface = documentOrId as DraftInterface
    if (typeof documentOrId === 'number') {
      await dispatch(
        actionCreatorAsync(InvoiceActions?.SET_INVOICE, async () => getDocument(documentType, documentOrId)),
      )
      invoice = getState()?.invoice?.invoicedocument || getState()?.invoice?.creditnotedocument
      invoice.invoiceId = getState()?.invoice?.id
      downloadXRechnung(getState()?.invoice)
    } else {
      dispatch(updateInvoiceAndDownloadXRechnung(invoice))
    }
  }
}

export const updateInvoiceAndDownloadXRechnung = (invoice: DraftInterface) => {
  return async (dispatch, getState) => {
    dispatch(showGeneratingDocumentSpinner())
    await dispatch(updateInvoice(invoice))
    dispatch(hideGeneratingDocumentSpinner())
    downloadXRechnung(getState()?.invoice)
    dispatch(setNavigation(getNavigationToList(invoice?.document, invoice?.id, getState())))
  }
}

const updateInvoiceAndPreparePDF = async (dispatch, getState, invoice: DraftInterface) => {
  dispatch(showGeneratingDocumentSpinner())
  await dispatch(updateInvoice(invoice))
  dispatch(hideGeneratingDocumentSpinner())
  return true
}

export const updateInvoiceAndSendPaper = (invoice: DraftInterface) => {
  return async (dispatch, getState) => {
    await updateInvoiceAndPreparePDF(dispatch, getState, invoice)
    await sendPaperInvoice(invoice, getState().invoice.id)
    await navigateAfterSending(dispatch, getState)
  }
}

export const updateInvoiceAndSendEInvoice = (invoice: DraftInterface, attachments: string[], isPublicEntity) => {
  return async (dispatch, getState) => {
    if (invoice.document === 'invoice') {
      await updateInvoiceAndPreparePDF(dispatch, getState, invoice)
      invoice = getState().invoice
    } else {
      await dispatch(
        actionCreatorAsync(InvoiceActions.SET_INVOICE, async () => getDocument(invoice.document, invoice.invoiceId)),
      )
    }
    if (!invoice.id) {
      invoice.id = invoice.invoiceId
    }
    await dispatch(navigateRemoveModal('SendEInvoiceModal'))
    await navigateAfterSending(dispatch, getState)
    await sendEInvoice(getState().invoice, attachments, isPublicEntity, invoice.document)
    await refreshDocument(invoice.id, invoice.document)
  }
}

// BUG-133 Make sure it has invoiceNumber field
const handleInvoiceForEdit = (invoice) => {
  if (!invoice.invoiceNumber) {
    const invoiceNumber = invoice.fields.invoiceNumber.value
    invoice.fields.invoiceNumber.value = invoiceNumber.toString()
    invoice.invoiceNumber = {
      number: invoiceNumber,
      prefix: null,
    }
  }
  return invoice
}

const handleInvoiceBeforeSave = (invoice, config, country, settings) => {
  invoice = handleFieldsBeforeSave(invoice, settings)
  invoice = handleInvoiceForEdit(invoice)
  setTypeForInvoiceDraft(invoice, config, country)
  return invoice
}

export function editInvoice(id: number, showCreditNoteWarning: boolean) {
  Analytics.trackEvent(`${Events.openDocument}`, {
    type: AnalyticsType.action,
    category: Category.document,
    result: Result.success,
    placement: Placement.invoices,
    documentType: AnalyticsObject.invoice,
    documentID: id,
  })

  return async (dispatch, getState) => {
    if (showCreditNoteWarning) {
      dispatch(
        navigateAddModal({
          component: 'EditInvoiceCreditNoteWarningModal',
          props: {
            invoiceId: id,
          },
        }),
      )
    } else {
      dispatch(deleteRecurringProfileSettings())
      navigateToEditor(dispatch, getState)
      dispatch(
        loadingEditorVitals(async () => {
          await dispatch(getInvoice(id))
          await dispatch(prepareInvoiceForEdit(id))
          await loadVitalsForEditor(dispatch, getState())
          // update invoice with company, customer
          const { settings, company } = getState()
          dispatch(updateDraftCompanyFromSettings(settings))
          dispatch(updateDraftCompanyFromCompany(company))
          dispatch(updateDraftStylesFromSettings(settings))
          dispatch(updateDraftFieldsFromSettings(settings))
          const customer: CustomerInterface =
            getState().customers.collection?.find((c) => c.id === getState()?.draft?.receiver?.id) ||
            getState()?.draft?.receiver
          if (customer) {
            dispatch(updateDraftCustomer(customer, true))
          }
        }),
      )
    }
  }
}

export const prepareInvoiceForEdit: ActionCreator<number> = actionCreator(InvoiceActions.EDIT_INVOICE)

export function getInvoiceCopy(id: number): AsyncAction<InvoiceInterface> {
  return actionCreatorAsync<InvoiceInterface>(InvoiceActions.COPY_INVOICE, async () => api.getInvoice(id))
}

export const copyInvoice = (id: number) => {
  return async (dispatch, getState) => {
    dispatch(
      loadingEditorVitals(async (dispatch) => {
        const { settings } = getState()
        await dispatch(getInvoiceCopy(id))
        await dispatch(getInvoiceNumberSequences())
        await dispatch(updateInvoiceNumber(getState().invoiceNumbers.active))
        await loadVitalsForEditor(dispatch, getState())
        await dispatch(updatePaymentReferenceFromSettings(settings))
        dispatch(updateCustomerNumberFromSettings(settings))
        dispatch(updateDraftCompanyFromSettings(settings))
        dispatch(updateDraftStylesFromSettings(settings))
        dispatch(updateDraftCompanyFromCompany(getState().company))
        dispatch(updateDraftCustomerFromCustomers(getState().customers?.collection))
      }),
    )
    navigateToEditor(dispatch, getState)
  }
}

export function setStatus(id: number, documentType: DocumentTypes, statusData) {
  Analytics.trackEvent(Events.setDocumentStatus, {
    type: 'action',
    category: 'document',
    result: 'success',
    placement: 'invoices',
    documentType: 'invoice',
    documentID: id,
    newStatus: statusData.status,
  })
  return async () => {
    await api.setStatus(id, documentType, statusData)
  }
}

export function setExternalInvoiceStatus(id: number, status: 'paid' | 'open', invoiceData, onSuccess: () => void) {
  const invoiceStatusLockedError = 'Your accounts have been locked'
  return async () => {
    try {
      if (status === 'paid') {
        await api.setExternalInvoiceAsPaid(id, invoiceData)
      } else if (status === 'open') {
        await api.setExternalStatusAsUnpaid(id)
      }
      if (['paid', 'open'].includes(status)) {
        onSuccess()
      }
    } catch (error) {
      if (status === 'paid') {
        notifier('apix.ApixExternalPaymentError', 'warning')
      } else if (status === 'open') {
        const errorMessage = error.response?.data?.message
        if (error.response?.status === 500 && errorMessage.includes(invoiceStatusLockedError)) {
          notifier('api.ApixExternalLockedError', 'warning')
        } else {
          notifier('apix.ApixExternalVoidError', 'warning')
        }
      }
    }
  }
}

export const showInvoice = (id: number, documentType: DocumentTypes) => {
  return async (dispatch) => {
    dispatch(
      navigateAddModal({
        component: 'ShowDocumentModal',
        props: {
          content: getDocumentHTML(documentType, id),
          onClose: () => dispatch(navigateRemoveModal('ShowDocumentModal')),
        },
      }),
    )
  }
}

export const createCreditNote = (id: number) => {
  return async (dispatch, getState) => {
    dispatch(
      loadingEditorVitals(async (dispatch) => {
        const { settings, company } = getState()
        await dispatch(getInvoiceCopy(id))
        await dispatch(getInvoiceNumberSequences())
        await dispatch(updateInvoiceNumber(getState().invoiceNumbers.active))
        await loadVitalsForEditor(dispatch, getState())
        await dispatch(updatePaymentReferenceFromSettings(settings))
        dispatch(updateCustomerNumberFromSettings(settings))
        dispatch(updateDraftCompanyFromSettings(settings))
        dispatch(updateDraftStylesFromSettings(settings))
        dispatch(updateDraftCompanyFromCompany(company))
        const draft = Immutable.copy(getState().draft)
        const config = Immutable.copy(getState().config)
        const token = Immutable.copy(getState().token)

        const updatedDraft: DraftInterface = updateDraftForCreditNote(
          draft,
          id,
          config,
          Immutable.copy(getState().settings),
          Immutable.copy(getState().draft),
          token,
        )

        await dispatch(updateInvoiceDraftAction(updatedDraft))
        await dispatch(changeLines(updatedDraft.lines as any))
        await dispatch(updateInvoiceNumber(getState().invoiceNumbers.active))
      }),
    )
    navigateToEditor(dispatch, getState)
  }
}

export const notifyNewInvoiceNumber = () => {
  return async (dispatch, getState) => {
    // don't show modal if notification's been read already
    if (!localStorage.getItem('newInvoiceNumberNoticeRead')) {
      // don't show modal if first invoice number (2018001) already used
      const { company } = getState()
      if (company?.baseInvoiceNumber === 20180001) {
        dispatch(
          navigateAddModal({
            component: 'InvoiceNumberNotice',
            props: {
              invoiceNumber: company?.baseInvoiceNumber,
              memorizeNoticeRead: () => {
                localStorage.setItem('newInvoiceNumberNoticeRead', 'true')
              },
            },
          }),
        )
      }
    }
  }
}
